import {
  createStyles,
  IconButton,
  InputAdornment,
  TextField,
  withStyles,
  WithStyles,
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Close from '@material-ui/icons/Close';
import React, { useEffect } from 'react';
import { useArrayState } from 'react-use-object-state';
import { JupiterTheme } from '../../theme/types';

const styles = (theme: JupiterTheme) =>
  createStyles({
    container: { width: '100%' },
    input: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    helpIcon: {
      color: theme.palette.grey[400],
      marginBottom: theme.spacing(1),
    },
    adornment: {
      marginRight: 0,
      marginLeft: theme.spacing(0.5),
    },
    tooltip: {
      padding: theme.spacing(2),
      wordBreak: 'break-word',
    },
    inputGroup: {
      display: 'flex',
      marginTop: theme.spacing(2),
    },
    valueSection: {
      marginBottom: theme.spacing(3),
    },
  });

interface Props extends WithStyles<typeof styles> {
  inputLabel: string;
  inputPlaceholder?: string;
  onUpdate: (values: string[]) => void;
  initialValues?: string[];
  disabled?: boolean;
}

export const ArrayInput = ({
  classes,
  onUpdate,
  inputLabel,
  inputPlaceholder,
  initialValues,
  disabled,
}: Props) => {
  const arrayValues = useArrayState<string>([]);

  useEffect(() => {
    onUpdate(arrayValues.state);
  }, [arrayValues.state]);

  useEffect(() => {
    if (initialValues) {
      arrayValues.setState(initialValues);
    }
  }, []);

  const handleAddValue = () => {
    arrayValues.push('');
  };

  const handleKeyPress = (e: any) => {
    if (e.key === 'Enter') handleAddValue();
  };

  const handleDeleteValue = (i: number) => {
    return (e: any) => {
      arrayValues.setState(
        arrayValues.state.filter((_s: string, index: number) => index !== i)
      );
    };
  };

  const handleUpdateExistingValue = (index: number) => {
    return (e: any) => {
      arrayValues.upsertAt(e.target.value, index);
    };
  };

  const handleUpdateValue = (e: any) => {
    if (arrayValues.state.length < 1) {
      arrayValues.push(e.target.value);
    }
    handleUpdateExistingValue(arrayValues.state.length - 1)(e);
  };

  const items = [...arrayValues.state];
  items.pop();

  return (
    <div className={classes.container}>
      <TextField
        data-testid="ArrayInput"
        className={classes.input}
        label={inputLabel}
        value={arrayValues.state[arrayValues.state.length - 1] || ''}
        onChange={handleUpdateValue}
        onKeyPress={handleKeyPress}
        placeholder={inputPlaceholder}
        disabled={disabled}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={
          disabled
            ? undefined
            : {
                endAdornment: (
                  <InputAdornment
                    classes={{ root: classes.adornment }}
                    position="start"
                  >
                    <IconButton onClick={handleAddValue}>
                      <Add />
                    </IconButton>
                  </InputAdornment>
                ),
              }
        }
      />
      <div>
        {(items || []).map((item: string, i: number) => {
          return (
            <div className={classes.inputGroup} key={i}>
              <TextField
                value={item}
                onChange={handleUpdateExistingValue(i)}
                name={name + '-label'}
                className={classes.input}
                disabled={disabled}
              />
              <div>
                <IconButton onClick={handleDeleteValue(i)}>
                  <Close />
                </IconButton>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default withStyles(styles as any)(ArrayInput);
export { ArrayInput as ArrayInputWithoutStyles };
